import opsActivityMatches from '@/function/ops/ops.activity.matches';
import opsDeployOutcome from '@/function/ops/ops.deploy.outcome';
import { OPS_LOGS_WINDOW } from '@/function/ops/ops.live.constants';
import opsLogsMerge from '@/function/ops/ops.logs.merge';
import opsPagesUpsert from '@/function/ops/ops.pages.upsert';
import opsServersMerge from '@/function/ops/ops.servers.merge';
import { type TypeOpsAlertsQuery, type TypeOpsServerQuery, opsApi, run, withClock } from '@/store/api/ops/ops.api';
import type { TypeAppDispatch, TypeRootState } from '@/store/store';
import type { TypeOpsActionRun, TypeOpsRun } from '@/types/ops/types.ops.action';
import type { TypeOpsActivity, TypeOpsActivityQuery } from '@/types/ops/types.ops.activity';
import type { TypeOpsAlert } from '@/types/ops/types.ops.alert';
import type { TypeOpsDeploy } from '@/types/ops/types.ops.deploy';
import type { TypeOpsLogsBatch } from '@/types/ops/types.ops.log';
import type { TypeOpsRunnerKey, TypeOpsServerRegistry, TypeOpsServersWire } from '@/types/ops/types.ops.server';
import { original } from '@reduxjs/toolkit';

const STATUS_SETTLE_MS = 20_000;

export const cachedActions = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'pageOpsActions') as TypeOpsServerQuery[];

export const cachedRuns = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'pageOpsRuns') as TypeOpsServerQuery[];

export const cachedAlerts = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'pageOpsAlerts') as TypeOpsAlertsQuery[];

export const cachedDeploys = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'pageOpsDeploys') as TypeOpsServerQuery[];

export const cachedActivity = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'pageOpsActivity') as TypeOpsActivityQuery[];

export const cachedStatus = (getState: () => TypeRootState) => opsApi.util.selectCachedArgsForQuery(getState(), 'readOpsDeployStatus') as string[];

const idOf = (item: { id: string }) => item.id;

const ofServer = <T extends { server: string }>(server: string) => (item: T) => !server || item.server === server;

export const writeOpsActions = (dispatch: TypeAppDispatch, wire: { runs?: TypeOpsActionRun[] }): void => {
    if (!wire || !Array.isArray(wire.runs)) return;
    const runs = wire.runs;
    run(dispatch, (inner, getState) => {
        for (const args of cachedActions(getState)) {
            const mine = runs.filter(ofServer(args.server));
            if (mine.length) inner(opsApi.util.updateQueryData('pageOpsActions', args, (draft) => opsPagesUpsert(draft.pages, [...mine].reverse(), idOf, () => true)));
        }
    });
};

export const addOpsActionRun = (dispatch: TypeAppDispatch, item: TypeOpsActionRun): void => {
    run(dispatch, (inner, getState) => {
        for (const args of cachedActions(getState)) {
            if (ofServer(args.server)(item)) inner(opsApi.util.updateQueryData('pageOpsActions', args, (draft) => opsPagesUpsert(draft.pages, [item], idOf, () => true)));
        }
    });
};

export const writeOpsRuns = (dispatch: TypeAppDispatch, wire: { runs?: TypeOpsRun[] }): void => {
    if (!wire || !Array.isArray(wire.runs)) return;
    const runs = wire.runs;
    run(dispatch, (inner, getState) => {
        for (const args of cachedRuns(getState)) {
            const mine = runs.filter(ofServer(args.server));
            if (mine.length) inner(opsApi.util.updateQueryData('pageOpsRuns', args, (draft) => opsPagesUpsert(draft.pages, [...mine].reverse(), idOf, () => true)));
        }
    });
};

export const addOpsRun = (dispatch: TypeAppDispatch, item: TypeOpsRun): void => {
    run(dispatch, (inner, getState) => {
        for (const args of cachedRuns(getState)) {
            if (ofServer(args.server)(item)) inner(opsApi.util.updateQueryData('pageOpsRuns', args, (draft) => opsPagesUpsert(draft.pages, [item], idOf, () => true)));
        }
    });
};

export const writeOpsAlerts = (dispatch: TypeAppDispatch, wire: { alerts?: TypeOpsAlert[] }): void => {
    if (!wire || !Array.isArray(wire.alerts) || !wire.alerts.length) return;
    const alerts = wire.alerts;
    run(dispatch, (inner, getState) => {
        for (const args of cachedAlerts(getState)) {
            const mine = alerts.filter(ofServer(args.server));
            if (mine.length) inner(opsApi.util.updateQueryData('pageOpsAlerts', args, (draft) => opsPagesUpsert(draft.pages, mine, idOf, (alert) => !args.state || alert.state === args.state)));
        }
    });
};

export const refetchStatus = (inner: TypeAppDispatch, getState: () => TypeRootState, servers: readonly string[]): void => {
    const cached = cachedStatus(getState);
    for (const server of servers) {
        if (cached.includes(server)) inner(opsApi.endpoints.readOpsDeployStatus.initiate(server, { forceRefetch: true })).unsubscribe();
    }
};

export const writeOpsDeploys = (dispatch: TypeAppDispatch, wire: { deploys?: TypeOpsDeploy[] }): void => {
    if (!wire || !Array.isArray(wire.deploys) || !wire.deploys.length) return;
    const deploys = wire.deploys;
    const servers = Array.from(new Set(deploys.map((deploy) => deploy.server)));

    for (const server of servers) {
        const mine = deploys.filter((entry) => entry.server === server);
        dispatch(opsApi.util.updateQueryData('readOpsDeployStatus', server, (draft) => {
            for (const deploy of mine) {
                const target = draft.targets.find((entry) => entry.target === deploy.target);
                if (target) target.running = !opsDeployOutcome(deploy).final;
            }
        }));
    }

    run(dispatch, (inner, getState) => {
        const settled = new Set<string>();
        for (const args of cachedDeploys(getState)) {
            const mine = deploys.filter(ofServer(args.server));
            if (!mine.length) continue;
            const known = opsApi.endpoints.pageOpsDeploys.select(args)(getState()).data?.pages.flatMap((page) => page.items) ?? [];
            for (const deploy of mine) {
                const before = known.find((entry) => entry.id === deploy.id);
                if (opsDeployOutcome(deploy).final && (!before || !opsDeployOutcome(before).final)) settled.add(deploy.server);
            }
            inner(opsApi.util.updateQueryData('pageOpsDeploys', args, (draft) => opsPagesUpsert(draft.pages, mine, idOf, () => true)));
        }
        if (!settled.size) return;
        const names = [...settled];
        refetchStatus(inner, getState, names);
        setTimeout(() => refetchStatus(inner, getState, names), STATUS_SETTLE_MS);
    });
};

export const addOpsDeploy = (dispatch: TypeAppDispatch, deploy: TypeOpsDeploy): void => writeOpsDeploys(dispatch, { deploys: [deploy] });

export const writeOpsActivity = (dispatch: TypeAppDispatch, wire: { items?: TypeOpsActivity[] }): void => {
    if (!wire || !Array.isArray(wire.items) || !wire.items.length) return;
    const items = wire.items.filter((item) => item && typeof item.id === 'string' && typeof item.ts === 'string');
    run(dispatch, (inner, getState) => {
        for (const args of cachedActivity(getState)) {
            const mine = items.filter((item) => opsActivityMatches(item, args)).sort((a, b) => a.ts.localeCompare(b.ts));
            if (mine.length) inner(opsApi.util.updateQueryData('pageOpsActivity', args, (draft) => opsPagesUpsert(draft.pages, mine, idOf, () => true)));
        }
    });
};

export const writeOpsRegistry = (dispatch: TypeAppDispatch, registry: TypeOpsServerRegistry): void => {
    dispatch(opsApi.util.updateQueryData('readOpsServers', undefined, (draft) => {
        const entry = draft.servers.find((server) => server.server === registry.name);
        if (entry) entry.registry = registry;
        else draft.servers.push({ server: registry.name, registry, staleAfterSeconds: 45, snapshot: null, history: [] });
        draft.servers.sort((a, b) => a.server.localeCompare(b.server));
    }));
};

export const writeOpsRunnerKey = (dispatch: TypeAppDispatch, name: string, runnerKey: TypeOpsRunnerKey | null): void => {
    dispatch(opsApi.util.updateQueryData('readOpsServers', undefined, (draft) => {
        const entry = draft.servers.find((server) => server.server === name);
        if (entry?.registry) entry.registry.runnerKey = runnerKey;
    }));
};

export const dropOpsServer = (dispatch: TypeAppDispatch, name: string): void => {
    dispatch(opsApi.util.updateQueryData('readOpsServers', undefined, (draft) => {
        draft.servers = draft.servers.filter((server) => server.server !== name);
    }));
};

export const writeOpsServers = (dispatch: TypeAppDispatch, wire: TypeOpsServersWire): void => {
    if (!wire || !Array.isArray(wire.servers)) return;
    dispatch(opsApi.util.updateQueryData('readOpsServers', undefined, (draft) => {
        const fresh = withClock(wire);
        const base = original(draft);
        return { ...fresh, servers: base ? opsServersMerge(base.servers, fresh.servers) : fresh.servers };
    }));
};

export const writeOpsLogs = (dispatch: TypeAppDispatch, batch: TypeOpsLogsBatch): void => {
    if (!batch || typeof batch.server !== 'string' || typeof batch.container !== 'string' || !Array.isArray(batch.entries)) return;

    dispatch(opsApi.util.updateQueryData('readOpsLogs', { server: batch.server, container: batch.container }, (draft) => {
        draft.entries = opsLogsMerge(draft.entries, batch.entries, OPS_LOGS_WINDOW);
        if (batch.gap === true) draft.gaps += 1;
    }));
};
